import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DrillCardModal } from './admin/AdminWorkflows';
import { UsaDigsTab } from './admin/AdminBidDetail';
import { jobName } from '../utils/ui';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }
function apiFetch(path, opts = {}) {
  return fetch(`${API_BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', ...(getToken() ? { 'x-worker-token': getToken() } : {}), ...opts.headers } })
    .then(r => r.ok ? r.json() : Promise.reject());
}

const TYPE_META = {
  vertical_drilling: { label: 'Vertical Drilling', icon: '🕳️', color: '#1e3a5f' },
  horizontal_drilling: { label: 'Directional Drilling', icon: '↔️', color: '#4a1d50' },
  loops: { label: 'Loops', icon: '🔁', color: '#059669' },
  nve: { label: 'NVE', icon: '⚡', color: '#d97706' },
  usa_digs: { label: 'USA Digs', icon: '🚧', color: '#7c2d12' },
  shop: { label: 'Shop', icon: '🔧', color: '#475569' },
  submittals: { label: 'Submittals', icon: '📋', color: '#1e3a5f' },
  contract: { label: 'Contract', icon: '📄', color: '#0f766e' },
  payroll: { label: 'Payroll', icon: '💵', color: '#b45309' },
};
const meta = t => TYPE_META[t] || { label: (t || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), icon: '📁', color: '#374151' };
const isDrill = t => t === 'vertical_drilling' || t === 'horizontal_drilling';
const STATUS_LABEL = { pending: 'Not Started', active: 'In Progress', on_hold: 'On Hold', complete: 'Complete', closed: 'Closed' };   // closed = off the board, kept on the job (Oct 5 2026)
// These boards call an active job Scheduled — see AdminWorkflows for why. Kept in
// step here: work that reads Scheduled on the board must not read In Progress on the
// project page. Small jobs is not in the list; it keeps the ordinary wording.
const SAYS_SCHEDULED = ['vertical_drilling', 'horizontal_drilling', 'loops'];
const statusLabelFor = (w) => (SAYS_SCHEDULED.includes(w.type) && w.status === 'active') ? 'Scheduled' : (STATUS_LABEL[w.status] || w.status || 'pending');

export default function ProjectWorkflows() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [drillCard, setDrillCard] = useState(null);
  const [usaSj, setUsaSj] = useState(null); // job's USA dig tickets, opened in an overlay

  function load() { apiFetch(`/workflows?project=${encodeURIComponent(id)}`).then(all => setRows((Array.isArray(all) ? all : []).filter(w => String(w.project_id) === String(id)))).catch(() => setRows([])); }
  useEffect(() => { load(); }, [id]);

  const projectName = rows && rows[0] ? rows[0].project_name : '';
  const projectNumber = rows && rows[0] ? rows[0].project_number : '';

  return (
    <div className="page" style={{ background: '#f7f7f7' }}>
      <div className="top-bar">
        <button onClick={() => navigate(`/project/${id}`)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: '#fff' }}>
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M14 4l-7 7 7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <div className="top-bar-wordmark" style={{ flex: 1, textAlign: 'center' }}>TITAN <span>ELECTRIC</span></div>
        <div style={{ width: 22 }} />
      </div>

      <div style={{ padding: '18px 16px 100px' }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#e87722', textTransform: 'uppercase', letterSpacing: 0.5 }}>Work Flows</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#111', marginBottom: 16 }}>{projectNumber ? projectNumber + ' · ' : ''}{jobName(projectName) || 'This Job'}</div>

        {rows === null ? (
          <div style={{ color: '#9ca3af', textAlign: 'center', padding: '30px 0' }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ color: '#9ca3af', textAlign: 'center', padding: '30px 0', fontSize: 14 }}>No workflow categories on this job yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rows.map(w => {
              const m = meta(w.type);
              const holes = isDrill(w.type) && w.hole_count > 0;
              return (
                <button key={w.id}
                  onClick={() => isDrill(w.type) ? setDrillCard(w) : w.type === 'usa_digs' ? setUsaSj(w) : navigate(`/sup/workflows/${w.type}`)}
                  style={{ width: '100%', textAlign: 'left', background: '#fff', border: '1.5px solid #ececec', borderLeft: `5px solid ${m.color}`, borderRadius: 12, padding: '14px 16px', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 20 }}>{m.icon}</span>
                    <span style={{ flex: 1, fontSize: 16, fontWeight: 800, color: '#111' }}>{w.name || m.label}</span>
                    <span style={{ fontSize: 18, color: '#c9c9c9' }}>›</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: '#374151', background: '#f3f4f6', padding: '2px 9px', borderRadius: 10 }}>{statusLabelFor(w)}</span>
                    {isDrill(w.type) && w.drill_rig && <span style={{ fontSize: 11.5, fontWeight: 700, color: '#92400e', background: '#fef3c7', padding: '2px 9px', borderRadius: 10 }}>🛠️ {w.drill_rig}</span>}
                    {holes && <span style={{ fontSize: 11.5, fontWeight: 700, color: '#1e3a5f', background: '#eff6ff', padding: '2px 9px', borderRadius: 10 }}>🕳️ {w.holes_done}/{w.hole_count}</span>}
                    {w.type === 'nve' && w.nve_count != null && <span style={{ fontSize: 11.5, fontWeight: 700, color: '#b45309', background: '#fffbeb', padding: '2px 9px', borderRadius: 10 }}>{w.nve_count} meters</span>}
                    {isDrill(w.type) && <span style={{ fontSize: 11, color: '#9ca3af' }}>tap to work it</span>}
                    {w.type === 'usa_digs' && <span style={{ fontSize: 11, color: '#9ca3af' }}>tap to open tickets</span>}
                    {!isDrill(w.type) && w.type !== 'usa_digs' && <span style={{ fontSize: 11, color: '#9ca3af' }}>tap to open board</span>}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {drillCard && (
        <DrillCardModal card={drillCard} pmName={() => ''} onClose={() => setDrillCard(null)} onChanged={load} />
      )}

      {usaSj && (
        <div style={{ position: 'fixed', inset: 0, background: '#f7f7f7', zIndex: 5000, display: 'flex', flexDirection: 'column' }}>
          <div className="top-bar">
            <button onClick={() => { setUsaSj(null); load(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: '#fff' }}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M14 4l-7 7 7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <div className="top-bar-wordmark" style={{ flex: 1, textAlign: 'center' }}>USA DIG TICKETS</div>
            <div style={{ width: 22 }} />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
            <UsaDigsTab sjId={usaSj.id} isAdminZone={true} API_BASE={API_BASE} getToken={getToken}
              showToast={(m, t) => { if (t === 'error' && m) window.alert(m); }} />
          </div>
        </div>
      )}
    </div>
  );
}
